import {
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  AutocompleteInput,
  useGetList,
} from 'react-admin'
import { Paper } from '@mui/material'
import { useState, useEffect, useRef } from 'react'

export const PaperArrayReferenceInputWithSearch = ({ label, source, reference }) => {
  const [perPage, setPerPage] = useState(100)
  const [searchText, setSearchText] = useState('')
  const isLoadingMoreRef = useRef(false)

  const { total } = useGetList(
    reference,
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'name', order: 'ASC' },
      filter: searchText ? { search: searchText } : {},
    },
    { enabled: true }
  )

  useEffect(() => {
    setPerPage(100)
    isLoadingMoreRef.current = false
  }, [searchText])

  useEffect(() => {
    const loadMoreRecords = (currentPerPage) => {
      const currentHasMore = total ? currentPerPage < total : true
      if (currentHasMore) {
        isLoadingMoreRef.current = true
        setTimeout(() => {
          isLoadingMoreRef.current = false
        }, 500)
        return currentPerPage + 100
      }
      return currentPerPage
    }

    let scrollCleanup = null

    const addScrollListener = () => {
      const listbox = document.querySelector('[role="listbox"]')
      if (!listbox || listbox.dataset.scrollListenerAdded === 'true') return

      listbox.dataset.scrollListenerAdded = 'true'

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = listbox
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

        if (scrollPercentage > 0.8 && !isLoadingMoreRef.current) {
          setPerPage(loadMoreRecords)
        }
      }

      listbox.addEventListener('scroll', handleScroll)

      scrollCleanup = () => {
        listbox.removeEventListener('scroll', handleScroll)
        delete listbox.dataset.scrollListenerAdded
      }
    }

    const timers = [
      setTimeout(addScrollListener, 100),
      setTimeout(addScrollListener, 300),
      setTimeout(addScrollListener, 500),
    ]

    const observer = new MutationObserver(addScrollListener)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      timers.forEach(clearTimeout)
      observer.disconnect()
      if (scrollCleanup) scrollCleanup()
    }
  }, [total])

  return (
    <Paper
      sx={{
        p: 3,
        mb: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <ArrayInput source={source} label={label}>
        <SimpleFormIterator
          sx={{
            '& > ul, & .RaSimpleFormIterator-list': {
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 2,
              listStyle: 'none',
              padding: 0,
              margin: 0,
            },
            '& .RaSimpleFormIterator-line': {
              margin: 0,
              width: '100%',
              backgroundColor: 'background.default',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              padding: 2,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '& .RaSimpleFormIterator-action': {
                marginLeft: 'auto',
                flexShrink: 0,
              },
              '& > div:first-of-type': {
                flex: 1,
                minWidth: 0,
              },
            },
          }}
        >
          <ReferenceInput
            source=""
            reference={reference}
            fullWidth
            perPage={perPage}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <AutocompleteInput
              optionText="name"
              filterToQuery={(searchText) => {
                setSearchText(searchText || '')
                return { search: searchText }
              }}
              fullWidth
            />
          </ReferenceInput>
        </SimpleFormIterator>
      </ArrayInput>
    </Paper>
  )
}
